/**
 * Job hooks (§5.2 "Jobs", §10) plus `useTrackJob`, which follows any job with a
 * progress toast (use it right after an endpoint returns a `JobBrief`).
 * Owner: frontend-foundation.
 */
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from '@/components/toast';
import { isUserActive, pollInterval, useUserActive } from '@/lib/activity';
import { humanize } from '@/lib/format';
import { api, errorMessage } from '../client';
import { qk } from '../keys';
import type { Job, JobBrief, JobListParams, JobPage, JobStatus } from '../types';

/** Poll interval while something is queued/running. */
export const JOB_POLL_MS = 2000;
/** Running jobs, while the user is idle (nothing polls when idle and nothing runs). */
export const JOB_IDLE_POLL_MS = 30_000;
/** Top-bar check for new jobs while the user is active and nothing runs. */
export const JOBS_QUIET_POLL_MS = 15_000;

export function isJobActive(status: JobStatus): boolean {
  return status === 'queued' || status === 'running';
}

export function isJobTerminal(status: JobStatus): boolean {
  return !isJobActive(status);
}

export const jobsQuery = (params: JobListParams) =>
  queryOptions({
    queryKey: qk.jobs.list(params),
    queryFn: ({ signal }) => api.get<JobPage>('/api/jobs', { signal, query: params }),
  });

/**
 * A page of jobs (newest first). With `autoRefresh`, it re-polls every
 * {@link JOB_POLL_MS} while any listed job (or batch child) is still active.
 */
export function useJobs(params: JobListParams, options: { autoRefresh?: boolean } = {}) {
  const userActive = useUserActive();
  return useQuery({
    ...jobsQuery(params),
    placeholderData: (previous) => previous,
    refetchInterval: (query) => {
      if (!options.autoRefresh) return false;
      const items = query.state.data?.items ?? [];
      const active = items.some(
        (job) =>
          isJobActive(job.status) ||
          (job.children_summary !== null && job.children_summary.queued + job.children_summary.running > 0),
      );
      return pollInterval(userActive, active, { busyMs: JOB_POLL_MS, idleBusyMs: JOB_IDLE_POLL_MS });
    },
  });
}

export const jobQuery = (jid: number) =>
  queryOptions({
    queryKey: qk.jobs.detail(jid),
    queryFn: ({ signal }) => api.get<Job>(`/api/jobs/${jid}`, { signal }),
  });

/** One job; polls until it is finished. */
export function useJob(jid: number | null) {
  const userActive = useUserActive();
  return useQuery({
    ...jobQuery(jid ?? 0),
    enabled: jid !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      const active = status === undefined || isJobActive(status);
      return pollInterval(userActive, active, { busyMs: JOB_POLL_MS, idleBusyMs: JOB_IDLE_POLL_MS });
    },
  });
}

/**
 * Number of running jobs visible to the user (top-bar indicator). Polls fast while jobs run,
 * every {@link JOBS_QUIET_POLL_MS} while the user is active, and not at all once the user
 * has been idle for 5 minutes (an open tab must not keep the server awake); input or focus
 * refreshes it at once.
 */
export function useRunningJobs() {
  const userActive = useUserActive();
  const query = useQuery({
    ...jobsQuery({ status: 'running', limit: 20 }),
    refetchInterval: (q) =>
      pollInterval(userActive, (q.state.data?.total ?? 0) > 0, {
        busyMs: JOB_POLL_MS,
        quietMs: JOBS_QUIET_POLL_MS,
        idleBusyMs: JOB_IDLE_POLL_MS,
      }),
  });
  const { refetch } = query;
  const wasActive = useRef(userActive);
  useEffect(() => {
    if (userActive && !wasActive.current) void refetch();
    wasActive.current = userActive;
  }, [userActive, refetch]);
  return query;
}

export function useCancelJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jid: number) => api.post<Job>(`/api/jobs/${jid}/cancel`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.jobs.all() }),
    meta: { errorTitle: 'Could not cancel the job' },
  });
}

/** Re-queues a failed/cancelled job as a new job (returned). */
export function useRetryJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jid: number) => api.post<Job>(`/api/jobs/${jid}/retry`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk.jobs.all() }),
    meta: { errorTitle: 'Could not retry the job' },
  });
}

// --------------------------------------------------------------------------- job toasts

export interface TrackJobOptions {
  /** Toast title while running (default: "<Kind> #<id>"). */
  title?: string;
  /** Toast title on success (default: "<title> finished"). */
  successTitle?: string;
  /** Queries to invalidate once the job succeeded (e.g. `qk.images.lists(pid)`). */
  invalidate?: readonly QueryKey[];
  onSuccess?: (job: Job) => void;
}

/**
 * Polls `job` until it finishes, mirroring progress in one toast; on success invalidates
 * `invalidate` and calls `onSuccess`. Resolves with the final job.
 */
export async function trackJob(
  queryClient: QueryClient,
  job: Pick<JobBrief, 'id' | 'kind'>,
  options: TrackJobOptions = {},
): Promise<Job | null> {
  const title = options.title ?? `${humanize(job.kind)} #${job.id}`;
  const toastId = toast.show({ title, description: 'Queued', progress: null, duration: null });
  for (;;) {
    let current: Job;
    try {
      current = await queryClient.fetchQuery({ ...jobQuery(job.id), staleTime: 0 });
    } catch (error) {
      toast.update(toastId, {
        tone: 'error',
        description: errorMessage(error),
        progress: undefined,
        duration: 8000,
      });
      return null;
    }
    if (isJobActive(current.status)) {
      toast.update(toastId, {
        description: current.message ?? humanize(current.status),
        progress: current.status === 'running' ? current.progress : null,
      });
      await new Promise((resolve) => setTimeout(resolve, isUserActive() ? JOB_POLL_MS : JOB_IDLE_POLL_MS));
      continue;
    }
    await queryClient.invalidateQueries({ queryKey: qk.jobs.all() });
    if (current.status === 'succeeded') {
      await Promise.all(
        (options.invalidate ?? []).map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      );
      options.onSuccess?.(current);
      toast.update(toastId, {
        tone: 'success',
        title: options.successTitle ?? `${title} finished`,
        description: current.message ?? undefined,
        progress: undefined,
        duration: 5000,
      });
    } else {
      toast.update(toastId, {
        tone: current.status === 'failed' ? 'error' : 'warning',
        title: `${title} ${current.status}`,
        description: current.error ?? current.message ?? undefined,
        progress: undefined,
        duration: 10_000,
      });
    }
    return current;
  }
}

/** `trackJob` bound to the app's QueryClient. Fire-and-forget: `track(job, {…})`. */
export function useTrackJob() {
  const queryClient = useQueryClient();
  return useCallback(
    (job: Pick<JobBrief, 'id' | 'kind'>, options?: TrackJobOptions) => trackJob(queryClient, job, options),
    [queryClient],
  );
}
